<template>
  <div class="relative" :class="{ 'w-full': isExpanded }">
    <div
      v-show="shouldShowOverlay"
      class="search-overlay z-20"
      @click="closeSearch()"
    />

    <div
      v-if="!mobileOnly"
      class="search-desktop hidden lg:flex items-center justify-start w-full relative"
      :class="{ 'expanded': isExpanded }"
    >
      <div
        class="relative w-full z-30"
        :class="isExpanded ? 'w-full' : MAX_WIDTH_CLASS"
      >
        <b-input-group size="sm" class="w-full">
          <b-form-input
            v-model="query"
            :placeholder="searchPlaceholder"
            autocomplete="off"
            @input="onInputChange"
            @blur="collapseSearch"
            @focus="onFocus"
            @keydown.down.prevent="highlightNext"
            @keydown.up.prevent="highlightPrev"
            @keydown.enter.prevent="handleEnter"
            class="w-full"
          />

          <b-input-group-append>
            <button
              type="button"
              class="btn btn-link p-0 margin-navbar"
              :class="{ 'text-gray-600': isExpanded }"
              @click="isExpanded ? closeSearch() : goToSearch()"
              :aria-label="desktopButtonLabel"
            >
              <b-icon :icon="desktopButtonIcon" />
            </button>
          </b-input-group-append>
        </b-input-group>

        <ul
          v-show="hasSuggestions"
          class="autocomplete-dropdown z-30 rounded-xl shadow-lg roboto-slab ml-1"
          :style="desktopDropdownStyle"
        >
          <li
            v-for="(item, index) in suggestions"
            :key="item.url || item.title || index"
            @mousedown.prevent="selectSuggestion(item)"
            :class="getSuggestionItemClass(item, index)"
          >
            <span v-if="!item.see_more" class="title" v-html="highlightMatch(item.title)" />
            <span v-else>{{ item.title }}</span>
          </li>
        </ul>

        <div
          v-show="showNoResults"
          class="dropdown autocomplete-dropdown mobile p-4 rounded-xl shadow-lg text-center ml-1"
          id="desktop"
        >
          <p class="text-black text-base font-bold mb-1">{{ searchNotFound }}</p>
          <p class="text-black text-base mt-3 mb-3">{{ searchTryTopics }}</p>

          <div class="grid grid-cols-2 sm:grid-cols-2 gap-4 gap-x-8 items-start mt-6 ml-16 mb-3">
            <a
              v-for="(topic, index) in TOPICS"
              :key="topic.key"
              :href="getTopicUrl(topic.key)"
              class="flex flex-col lg:flex-row items-center text-black mt-4"
              :class="{ 'ml-32': index % 2 === 0 }"
            >
              <img
                :src="topic.icon"
                :alt="topic.label"
                loading="lazy"
                class="object-contain w-10 h-10"
              />
              <p class="text-base font-bold mt-3 ml-3">{{ topic.label }}</p>
            </a>
          </div>
        </div>
      </div>
    </div>

    <div class="flex lg:hidden items-center w-full" :class="{ 'mobile-search-icon-only': mobileOnly }">
      <button
        v-if="!showMobileSearch && !menuOpened"
        class="btn btn-link  mobile-search-icon-default"
        @click="openMobileSearch"
        aria-label="search"
      >
        <b-icon icon="search" />
      </button>


      <transition name="mobile-search-slide">
        <div v-if="showMobileSearch" class="w-full z-30 mobile-search-container">
          <div class="flex items-center search-mobile-input-wrapper">
            <div class="search-mobile-input-field">
              <input
                v-model="query"
                @input="onInputChange"
                @focus="showSuggestions = true"
                @keydown.enter.prevent="handleEnter"
                type="text"
                autocomplete="off"
                class="flex-1 text-white text-base font-light bg-transparent w-full"
              />

              <button
                type="button"
                class="flex items-center justify-center hover:text-gray-200 focus:outline-none mobile-search-close-btn"
                @click="closeMobileSearch"
                :aria-label="mobileButtonLabel"
              >
                <span class="close-menu">&times;</span>
              </button>
            </div>
          </div>

          <ul
            v-show="hasSuggestions"
            class="w-full z-20 search-mobile-results"
          >
            <li
              v-for="(item, index) in suggestions"
              :key="item.url || item.title || index"
              @mousedown.prevent="selectSuggestion(item)"
              :class="['py-2 cursor-pointer text-base', { 'font-bold': item.see_more, 'search-mobile-result-item': !item.see_more }]"
            >
              <div v-if="!item.see_more" class="text-white" v-html="highlightMatch(item.title)" />
              <div v-else>{{ item.title }}</div>
            </li>
          </ul>

          <div
            v-show="showNoResults"
            class="w-full p-4 text-center search-mobile-no-results z-20"
          >
            <p class="text-white text-sm font-bold mb-1">{{ searchNotFound }}</p>
            <p class="text-white text-sm mb-4">{{ searchTryTopics }}</p>

            <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 justify-items-center items-start">
              <a
                v-for="topic in TOPICS"
                :key="topic.key"
                :href="getTopicUrl(topic.key)"
                class="flex flex-col items-center text-white hover:text-orange-500 transition"
              >
                <img
                  :src="getTopicIcon(topic)"
                  :alt="topic.label"
                  loading="lazy"
                  class="object-contain"
                />
                <p class="text-white text-sm  font-bold mb-1 mt-3">{{ topic.label }}</p>
              </a>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>
<script>
import axios from 'axios';

const MIN_QUERY_LENGTH = 2;
const SCROLL_OFFSET_VALUE = 180;
const DESKTOP_BREAKPOINT = 1024;
const DEBOUNCE_DELAY = 250;
const DESKTOP_ITEM_HEIGHT = 52;
const DESKTOP_MAX_ITEMS = 8;
const MAX_WIDTH_CLASS = 'max-w-[240px]';

const TOPICS = [
  {
    key: 'artificial-intelligence',
    label: 'Artificial Intelligence',
    icon: '/images/default/artificial-intelligence.svg'
  },
  {
    key: 'media-literacy',
    label: 'Media Literacy',
    icon: '/images/default/media-literacy.svg'
  },
  {
    key: 'countering-disinformation',
    label: 'Countering Disinformation',
    icon: '/images/default/countering-disinformation.svg'
  },
  {
    key: 'positive-narratives',
    label: 'Positive Narratives',
    icon: '/images/default/positive-narratives.svg'
  },
  {
    key: 'digital-security',
    label: 'Digital Security',
    icon: '/images/default/digital-security.svg'
  },
  {
    key: 'resilience',
    label: 'Resilience',
    icon: '/images/default/resilience.svg'
  }
];

export default {
  props: {
    searchPlaceholder: { type: String, default: '' },
    searchRoute: { type: String, required: true },
    searchNotFound: { type: String, default: '' },
    searchTryTopics: { type: String, default: '' },
    mobileOnly: { type: [String, Boolean], default: false },
    menuOpened: { type: Boolean, default: false },
    searchOpened: { type: Boolean, default: false }
  },

  data() {
    return {
      MIN_QUERY_LENGTH,
      MAX_WIDTH_CLASS,
      TOPICS,
      query: '',
      isExpanded: false,
      showSuggestions: false,
      showMobileSearch: false,
      highlightedIndex: -1,
      suggestions: [],
      debounceTimeout: null,
      isLoading: false,
      windowWidth: window.innerWidth
    };
  },

  computed: {
    shouldShowOverlay() {
      const desktopOverlay = this.showSuggestions && (this.suggestions.length || (this.query.length >= MIN_QUERY_LENGTH && !this.suggestions.length));
      const mobileOverlay = this.showMobileSearch;

      return desktopOverlay || mobileOverlay;
    },

    hasSuggestions() {
      return this.showSuggestions && this.suggestions.length > 0;
    },

    showNoResults() {
      return this.query.length >= MIN_QUERY_LENGTH &&
             this.showSuggestions &&
             !this.suggestions.length &&
             !this.isLoading;
    },

    desktopButtonIcon() {
      return this.isExpanded ? 'x' : 'search';
    },

    desktopButtonLabel() {
      return this.isExpanded ? 'close' : 'search';
    },

    mobileButtonLabel() {
      return this.query ? 'clear' : 'search';
    },

    desktopDropdownStyle() {
      if (!this.hasSuggestions || this.windowWidth < DESKTOP_BREAKPOINT) {
        return null;
      }

      const displayedItems = Math.min(this.suggestions.length, DESKTOP_MAX_ITEMS);
      const itemsHeight = displayedItems * DESKTOP_ITEM_HEIGHT;
      const availableSpace = window.innerHeight - SCROLL_OFFSET_VALUE;
      const height = Math.min(itemsHeight, availableSpace);

      return {
        maxHeight: `${height}px`,
        overflowY: itemsHeight > availableSpace ? 'auto' : 'hidden'
      };
    }
  },

  mounted() {
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.debounceTimeout);
  },

  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    getSuggestionItemClass(item, index) {
      return {
        'see-more font-bold': item.see_more,
        'highlighted': index === this.highlightedIndex
      };
    },

    getTopicUrl(topicKey) {
      return `${this.searchRoute}?query=&topic[]=${encodeURIComponent(topicKey)}`;
    },

    getTopicIcon(topic) {
        return topic.icon.replace('.svg', '-orange.svg');
    },

    onFocus() {
      this.isExpanded = true;
      this.$emit('search-expanded', true);
    },

    collapseSearch() {
      if (!this.query.trim()) {
        this.resetSearch();
      }
    },

    closeSearch() {
      if (this.showMobileSearch) {
        this.closeMobileSearch();
      } else {
        this.query = '';
        this.suggestions = [];
        this.resetSearch();
      }
    },

    resetSearch() {
      this.isExpanded = false;
      this.showSuggestions = false;
      this.highlightedIndex = -1;
      this.$emit('search-expanded', false);
    },

    onInputChange() {
    clearTimeout(this.debounceTimeout);

      this.highlightedIndex = -1;

      if (this.query.length < MIN_QUERY_LENGTH) {
        this.suggestions = [];
        this.isLoading = false;
        return;
      }

      this.isLoading = true;
      this.debounceTimeout = setTimeout(this.fetchSuggestions, DEBOUNCE_DELAY);
    },

    async fetchSuggestions() {
      try {
        this.isLoading = true;
        const response = await axios.get('/search/autocomplete', {
          params: { query: this.query }
        });

        const autocompleteResults = Array.isArray(response.data)
          ? response.data
          : response.data.results || response.data.data || [];

        const searchResults = autocompleteResults.filter(item => !item.see_more);
        const hasResults = searchResults.length > 0;

        this.suggestions = hasResults || !autocompleteResults.some(item => item.see_more) ? autocompleteResults : [];
        this.showSuggestions = true;
        this.highlightedIndex = -1;
      } catch (error) {
        console.error('Autocomplete error:', error);
        this.suggestions = [];
      } finally {
        this.isLoading = false;
      }
    },

    selectSuggestion(item) {
      if (item?.url) {
        window.location.href = item.url;
        this.showSuggestions = false;
      } else {
        this.goToSearch();
      }
    },

    goToSearch() {
      const trimmed = this.query.trim();
      window.location.href = trimmed
        ? `${this.searchRoute}?query=${encodeURIComponent(trimmed)}`
        : this.searchRoute;
    },

    highlightNext() {
      if (this.suggestions.length) {
        this.highlightedIndex = (this.highlightedIndex + 1) % this.suggestions.length;
        this.ensureVisible();
      }
    },

    highlightPrev() {
      if (this.suggestions.length) {
        this.highlightedIndex = (this.highlightedIndex - 1 + this.suggestions.length) % this.suggestions.length;
        this.ensureVisible();
      }
    },

    handleEnter() {
      if (this.highlightedIndex >= 0 && this.suggestions[this.highlightedIndex]) {
        this.selectSuggestion(this.suggestions[this.highlightedIndex]);
      } else {
        this.goToSearch();
      }
    },

    ensureVisible() {
      this.$nextTick(() => {
        const list = this.$el.querySelector('.autocomplete-dropdown');
        const items = list?.querySelectorAll('li');

        if (list && items && items[this.highlightedIndex]) {
          const item = items[this.highlightedIndex];
          const itemTop = item.offsetTop;
          const itemBottom = itemTop + item.offsetHeight;

          if (itemTop < list.scrollTop) {
            list.scrollTop = itemTop;
          } else if (itemBottom > list.scrollTop + list.clientHeight) {
            list.scrollTop = itemBottom - list.clientHeight;
          }
        }
      });
    },

    openMobileSearch() {
      this.showMobileSearch = true;
      this.query = '';
      this.suggestions = [];
      this.$emit('mobile-search-opened', true);
    },

    closeMobileSearch() {
      this.showMobileSearch = false;
      this.suggestions = [];
      this.query = '';
      this.$emit('mobile-search-opened', false);
    },

    highlightMatch(text) {
      if (!this.query) {
        return text;
      }

      const escapedQuery = this.query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQuery})`, 'gi');
      return text.replace(regex, '<strong>$1</strong>');
    }
  }
};
</script>
